import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Unstable_Grid2"; // Grid version 2
import { useFieldArray, useForm } from "react-hook-form";
import {
  type OrderCardRetailerFees as OrderCardRetailerFeesType,
} from "../../types/orderCardRetailerFees";
import { OrderCardBase } from "./OrderCardBase";
import { AddRetailerFeeModal } from "./AddRetailerFeeModal";
import { useOrderCard } from "./OrderCardContext";
import InputField from "../../components/InputField";
import { OrderCardActionButton } from "./OrderCardActionButton";
import { Order } from "../../types/order";
import { InputAdornment } from "@mui/material";

type FormData = {
  fees: OrderCardRetailerFeesType["cardData"]["fees"];
};

const getDefaultValues = (orderCard: OrderCardRetailerFeesType): FormData => {
  return {
    fees: orderCard?.cardData.fees ?? [],
  };
};

interface OrderCardRetailerFeesProps {
  isTemplate: Order["isTemplate"];
  orderCard: OrderCardRetailerFeesType;
  expanded: boolean;
  groupIndex: number;
}

export const OrderCardRetailerFees: React.FC<OrderCardRetailerFeesProps> = ({
  isTemplate,
  orderCard,
  expanded,
  groupIndex,
}) => {
  const [isRetailerFeeModalOpen, setIsRetailerFeeModalOpen] = useState(false);
  const { saveOrderCard, setEditingOrderCard } = useOrderCard();

  const disabled = orderCard.isLocked;

  const { control, register, reset, getValues, watch } =
    useForm<FormData>({
      defaultValues: getDefaultValues(orderCard),
      reValidateMode: "onChange",
      mode: "onChange",
    });
  const { fields } = useFieldArray({ control, name: "fees" });
  const fees = watch("fees");

  const handleBlurField = () => {
    saveOrderCard(orderCard.orderCardGuid, getValues());
    setEditingOrderCard(null);
  };

  const handleEditingField = () => {
    setEditingOrderCard(orderCard);
  };

  const handleClearClick = () => {
    const clearedOrderCard: OrderCardRetailerFeesType = {
      ...orderCard,
      cardData: {
        ...orderCard.cardData,
        fees: orderCard.cardData.fees.map((fee) => {
          return { ...fee, feeAmount: 0 };
        }),
      },
    };
    const defaultValues = getDefaultValues(clearedOrderCard);
    reset(defaultValues);
    saveOrderCard(orderCard.orderCardGuid, defaultValues);
  };

  const handleAddFeeClick = () => {
    setIsRetailerFeeModalOpen(true);
  };

  const handleCloseAddFeeModal = () => {
    setIsRetailerFeeModalOpen(false);
  };

  const handleAddFeeSaveSuccessful = (
    orderCardFee: OrderCardRetailerFeesType
  ) => {
    const defaultValues = getDefaultValues(orderCardFee);
    reset(defaultValues);
    setIsRetailerFeeModalOpen(false);
  };

  useEffect(() => {
    reset(getDefaultValues(orderCard), { keepDirtyValues: true });
  }, [orderCard, reset]);

  return (
    <>
      <OrderCardBase
        action={
          !disabled &&
          !isTemplate && (
            <OrderCardActionButton onClick={handleAddFeeClick}>
              Add Fee
            </OrderCardActionButton>
          )
        }
        disabled={disabled}
        orderCard={orderCard}
        onClearClick={handleClearClick}
        expanded={expanded}
        groupIndex={groupIndex}
      >
        <Grid container spacing={2}>
          {fields.map((field, index) => {
            const fee = fees[index];
            return (
              <Grid key={fee.feeName} xs={12} sm={12} md={6}>
                <InputField
                  {...register(`fees.${index}.feeAmount`, {
                    valueAsNumber: true,
                    onBlur: handleBlurField,
                  })}
                  startAdornment={
                    <InputAdornment position="start">$</InputAdornment>
                  }
                  type="number"
                  intOnly={true}
                  label={`${fee.feeName}`}
                  onFocus={handleEditingField}
                  placeholder={`Enter ${fee.feeName}`}
                  fullWidth
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
            );
          })}
        </Grid>
      </OrderCardBase>
      {isRetailerFeeModalOpen && (
        <AddRetailerFeeModal
          isOpen={isRetailerFeeModalOpen}
          orderCardGuid={orderCard.orderCardGuid}
          onSaveSuccessful={handleAddFeeSaveSuccessful}
          onClose={handleCloseAddFeeModal}
        />
      )}
    </>
  );
};
